import { mulberry32, seedFrom, shuffle } from "./random.js";
/**
 * Carry the pattern on — a motif printed at the start of a strip, and the rest of it empty.
 *
 * The worksheet version is unmistakable: three rows of squares, a block of colour at the left, and
 * a child filling the rest by repeating what they can see. What makes it a different game from
 * `vs-sequence` — which also asks what continues a pattern — is that the unit here is
 * TWO-DIMENSIONAL. A sequence is read left to right and answered with one card; a motif has a
 * shape, and continuing it means holding a small picture and stamping it again in the right place.
 * A child who can finish `red, blue, red, blue` often cannot carry a three-row motif, because the
 * second one needs the unit seen as a unit.
 *
 * **The child paints cells rather than choosing an answer, and that is what makes the level
 * legible.** Where they break the motif says what they were doing: a child who gets the first
 * column right and drifts afterwards has the idea and lost their place; a child who fills every
 * cell the same colour never had the unit at all. A multiple-choice version records neither.
 *
 * **Three rows always, and never fewer than two columns.** Three rows on a phone leaves each cell
 * around 90 px, which is above the floor where a five-year-old lands where they aimed; a fourth row
 * puts them under it. Two columns is the other floor and it is about legibility rather than touch:
 * a block one cell wide is a bar, and a bar does not read as a picture that repeats.
 */
/** Colours a motif may use. The renderer holds the hues; a cell holds an index or `EMPTY`. */
export const TILING_COLORS = 4;
/** An unpainted cell. */
export const TILING_EMPTY = -1;
/** Rows in every strip. Fixed: see the note above about the touch floor. */
export const TILING_ROWS = 3;
export const TILING_MIN_DIFFICULTY = 1;
export const TILING_MAX_DIFFICULTY = 8;
/**
 * Eight levels: a wider motif, then more colours, then a fuller one.
 *
 * The strip never exceeds nine columns. Nine cells across a 360 px phone are about 36 px, which is
 * far under the touch floor — so the strip SCROLLS rather than shrinking, and the renderer keeps
 * the cells at a size a finger can hit. Growing past nine would make a trial a scrolling exercise.
 */
function recipeFor(difficulty) {
    switch (difficulty) {
        /**
         * Two columns and one repeat: the smallest thing that still looks like a tile.
         *
         * **The first version opened on a ONE-column motif and it was the wrong shape entirely.** A
         * block one cell wide and three tall is a bar, not a picture, so the strip read as three columns
         * of an ordinary grid and nothing about it said "this shape goes here again". A motif needs two
         * dimensions before it can look like a unit that repeats, which is the whole idea the first
         * level exists to introduce.
         *
         * One repeat rather than two, for the same reason and for a second one: stamping it once is the
         * simplest possible version of the task, and four columns is what fits a 360 px phone without
         * the strip having to be dragged. A level whose job is to be trivially easy must not be the one
         * that needs scrolling.
         */
        case 1:
            return { motifCols: 2, repeats: 1, colours: 1, density: 2 };
        case 2:
            return { motifCols: 2, repeats: 1, colours: 1, density: 3 };
        case 3:
            return { motifCols: 2, repeats: 1, colours: 2, density: 4 };
        case 4:
            return { motifCols: 3, repeats: 1, colours: 2, density: 5 };
        // From here the motif is stamped twice, which is where holding it becomes the work.
        case 5:
            return { motifCols: 3, repeats: 2, colours: 3, density: 6 };
        case 6:
            return { motifCols: 3, repeats: 2, colours: 3, density: 7 };
        case 7:
            return { motifCols: 3, repeats: 2, colours: 4, density: 7 };
        // The densest motif, in four colours. Nothing is added to the strip: what changes is how much
        // of the unit has to be held at once.
        default:
            return { motifCols: 3, repeats: 2, colours: 4, density: 8 };
    }
}
export function generateTiling(task, formId, trialIndex, difficulty) {
    if (difficulty < TILING_MIN_DIFFICULTY || difficulty > TILING_MAX_DIFFICULTY) {
        throw new Error(`difficulty ${difficulty} out of range for ${task.id}`);
    }
    const { motifCols, repeats, colours, density } = recipeFor(difficulty);
    const rows = TILING_ROWS;
    const cells = motifCols * rows;
    if (density > cells) {
        throw new Error(`difficulty ${difficulty} asks for ${density} cells in a ${cells}-cell motif`);
    }
    const rand = mulberry32(seedFrom(formId, trialIndex, difficulty));
    /**
     * The motif, as a `motifCols × rows` block.
     *
     * Drawn cell by cell from a shuffled order so the filled count is exactly what the level says.
     * A per-cell coin flip would make density a average rather than a property, and two trials at one
     * level would differ in difficulty for no reason anybody chose.
     */
    const order = shuffle(Array.from({ length: cells }, (_, i) => i), rand);
    const motif = new Array(cells).fill(TILING_EMPTY);
    const filled = order.slice(0, density);
    filled.forEach((cell, i) => {
        // The first `colours` cells take one colour each, so every colour in play is actually used —
        // otherwise a four-colour level can quietly draw a three-colour motif.
        motif[cell] = i < colours ? i : Math.floor(rand() * colours);
    });
    const cols = motifCols * (repeats + 1);
    const solution = [];
    const given = [];
    for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
            const value = motif[r * motifCols + (c % motifCols)];
            solution.push(value);
            given.push(c < motifCols ? value : TILING_EMPTY);
        }
    }
    return { rows, cols, motifCols, solution, given };
}
/** Cells the child has to fill: everything past the printed motif. */
export function tilingBlanks(stimulus) {
    const out = [];
    for (let r = 0; r < stimulus.rows; r++) {
        for (let c = stimulus.motifCols; c < stimulus.cols; c++)
            out.push(r * stimulus.cols + c);
    }
    return out;
}
/**
 * How many cells a finished strip has something in — which is NOT how many cells it has.
 *
 * **This lives here because getting it backwards made the game unfinishable.** The renderer decided
 * a strip was done when no blank cell was still empty; but a gap in the motif repeats, so a correct
 * strip always ends with empty cells in it. A child who did everything right sat in front of a
 * finished strip that never resolved, and their only ways out were to fill every cell, which scores
 * wrong, or to wait out the timeout, which is recorded as walking away.
 *
 * A renderer cannot be tested here and this can, which is the whole reason it moved: the assertion
 * below — that a finished strip always has fewer marks than it has cells — is the one that would
 * have caught it.
 */
export function cellsToPaint(stimulus) {
    return tilingBlanks(stimulus).filter((cell) => stimulus.solution[cell] !== TILING_EMPTY).length;
}
/**
 * The strip has to match the motif everywhere, including the cells left empty.
 *
 * An empty cell in the motif is part of the pattern rather than an absence of one — a chequer is
 * as much about its gaps as its squares — so leaving a cell blank where the motif is blank is
 * correct, and filling it is wrong. That is why the response is compared to the whole solution
 * rather than only to the cells the child touched.
 */
export function scoreTilingTrial(stimulus, painted) {
    return (painted.length === stimulus.solution.length &&
        stimulus.solution.every((value, i) => painted[i] === value));
}
//# sourceMappingURL=tiling.js.map